import React, { useEffect, useState } from 'react';
import { X, Trash2, ChevronRight } from 'lucide-react';
import { MarketingContent, MarketingContentPatch } from '../../types/marketing';
import { WorkflowTemplate } from '../../utils/marketingTemplates';
import StageChecklist from './StageChecklist';
import './ContentDetailPanel.css';

interface ContentDetailPanelProps {
  content: MarketingContent;
  template: WorkflowTemplate;
  currentUserId: string;
  onUpdate: (id: string, patch: MarketingContentPatch) => void | Promise<void>;
  onStageToggle: (item: MarketingContent, stageId: string, done: boolean) => void | Promise<void>;
  onStageNote: (item: MarketingContent, stageId: string, note: string) => void | Promise<void>;
  onDelete: (id: string) => void | Promise<void>;
  onClose: () => void;
}

const ContentDetailPanel: React.FC<ContentDetailPanelProps> = ({
  content,
  template,
  onUpdate,
  onStageToggle,
  onStageNote,
  onDelete,
  onClose,
}) => {
  const [titleDraft, setTitleDraft] = useState(content.title);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setTitleDraft(content.title);
    setConfirmDelete(false);
  }, [content.id, content.title]);

  // Esc closes the panel
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const completedCount = template.stages.filter(
    s => content.stageStatuses?.[s.id]?.status === 'done'
  ).length;
  const progressPct = Math.round((completedCount / template.stages.length) * 100);

  const handleTitleBlur = () => {
    const next = titleDraft.trim();
    if (next && next !== content.title) {
      onUpdate(content.id, { title: next });
    } else {
      setTitleDraft(content.title);
    }
  };

  return (
    <>
      <div className="mh-detail-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="mh-detail-panel" role="dialog" aria-label="Content detail">
        <header className="mh-detail-header">
          <div className="mh-detail-header-left">
            <span className="mh-detail-eyebrow">{template.name}</span>
            <input
              className="mh-detail-title-input"
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              onBlur={handleTitleBlur}
              onKeyDown={e => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') {
                  setTitleDraft(content.title);
                  (e.target as HTMLInputElement).blur();
                }
              }}
              placeholder="Untitled"
            />
          </div>
          <div className="mh-detail-header-actions">
            {confirmDelete ? (
              <div className="mh-detail-confirm">
                <span>Delete?</span>
                <button
                  type="button"
                  className="mh-detail-confirm-yes"
                  onClick={() => {
                    onDelete(content.id);
                    setConfirmDelete(false);
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className="mh-detail-confirm-no"
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="mh-detail-icon-btn"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete content"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              type="button"
              className="mh-detail-icon-btn"
              onClick={onClose}
              aria-label="Close panel"
              title="Close"
            >
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="mh-detail-progress">
          <div className="mh-detail-progress-track">
            <div
              className="mh-detail-progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="mh-detail-progress-label">
            {completedCount} of {template.stages.length} stages · {progressPct}%
          </span>
        </div>

        <div className="mh-detail-body pm-scroll">
          {/* Quick metadata grid */}
          <section className="mh-detail-section">
            <h4 className="mh-detail-section-title">Overview</h4>
            <div className="mh-detail-meta-grid">
              <MetaField
                label="Recording date"
                type="date"
                value={dateInputValue(content.recordingDate)}
                onChange={val => onUpdate(content.id, { recordingDate: dateFromInput(val) })}
              />
              <MetaField
                label="Publish date"
                type="date"
                value={dateInputValue(content.publishDate)}
                onChange={val => onUpdate(content.id, { publishDate: dateFromInput(val) })}
              />
              <MetaField
                label="Guest"
                type="text"
                placeholder="e.g. Jane Smith"
                value={content.guestName ?? ''}
                onChange={val => onUpdate(content.id, { guestName: val || null })}
              />
            </div>
          </section>

          {/* Stage checklist */}
          <section className="mh-detail-section">
            <h4 className="mh-detail-section-title">
              Workflow
              <ChevronRight size={12} className="mh-detail-section-arrow" />
              <span className="mh-detail-section-sub">{template.stages.length} stages</span>
            </h4>
            <StageChecklist
              content={content}
              template={template}
              onToggle={(stageId, done) => onStageToggle(content, stageId, done)}
              onStageNote={(stageId, note) => onStageNote(content, stageId, note)}
              onUpdateField={patch => onUpdate(content.id, patch)}
            />
          </section>
        </div>
      </aside>
    </>
  );
};

/* ── Reusable metadata field ─────────────────────── */

interface MetaFieldProps {
  label: string;
  type: 'date' | 'text' | 'url';
  value: string;
  placeholder?: string;
  onChange: (next: string) => void;
}

const MetaField: React.FC<MetaFieldProps> = ({ label, type, value, placeholder, onChange }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <label className="mh-detail-meta-row">
      <span className="mh-detail-meta-label">{label}</span>
      <input
        type={type}
        className="mh-detail-meta-input"
        value={draft}
        placeholder={placeholder}
        onChange={e => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== value) onChange(draft);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            setDraft(value);
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </label>
  );
};

/* ── Date helpers (Firestore Timestamp <-> date input string) ── */

function dateInputValue(ts: MarketingContent['recordingDate'] | MarketingContent['publishDate']): string {
  if (!ts || typeof (ts as { toDate?: () => Date }).toDate !== 'function') return '';
  return (ts as { toDate: () => Date }).toDate().toISOString().slice(0, 10);
}

function dateFromInput(val: string): Date | null {
  if (!val) return null;
  return new Date(val + 'T00:00:00');
}

export default ContentDetailPanel;
